import { ColumnDef, VisibilityState } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useLocalStorage } from '@texas/hooks/useLocalStorage';

// TanStack Table replaces dots in accessorKey with underscores when deriving
// the column id (e.g. "shipment.trackingNumber" → "shipment_trackingNumber").
// We must apply the same normalisation so our visibility keys match.
export function resolveColumnId(col: ColumnDef<any, any>): string | undefined {
  const raw = (col as any).id ?? (col as any).accessorKey;
  if (!raw) return undefined;
  return typeof raw === 'string' ? raw.replace(/\./g, '_') : String(raw);
}

function buildDefaultVisibility(
  columns: ColumnDef<any, any>[],
): VisibilityState {
  const defaults: VisibilityState = {};
  for (const col of columns) {
    const meta = col.meta as
      | { toggleable?: boolean; defaultVisible?: boolean }
      | undefined;
    if (!meta?.toggleable) continue;
    const id = resolveColumnId(col);
    if (!id) continue;
    defaults[id] = meta.defaultVisible !== false;
  }
  return defaults;
}

export function useColumnVisibility(
  tableId: string | undefined,
  userId: number | undefined,
  columns: ColumnDef<any, any>[],
): [
  VisibilityState,
  (
    updater: VisibilityState | ((prev: VisibilityState) => VisibilityState),
  ) => void,
] {
  const storageKey =
    tableId && userId ? `${userId}_${tableId}_columns` : undefined;

  const defaults = useMemo(() => buildDefaultVisibility(columns), [columns]);

  const [stored, setStored] = useLocalStorage<VisibilityState>(
    storageKey,
    defaults,
  );

  // Merge defaults with stored so newly added toggleable columns get their
  // default visibility if they are not yet present in the stored value.
  const merged = useMemo(
    () => ({ ...defaults, ...stored }),
    [defaults, stored],
  );

  return [merged, setStored];
}
